import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useOrganization } from '../../context/OrganizationContext'
import { APP_NAME } from '../../config/brand'
import { getCurrencyLabel } from '../../utils/currencies'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { SkeletonTable } from '../../components/ui/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table'
import { OrgStatusBadge, ORG_STATUS_OPTIONS } from '../../utils/orgStatus'

const PAGE_SIZE = 50

/** Marks an organization that still needs its contact person's phone number. */
function NoPhoneBadge() {
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 align-middle text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-500/30 dark:text-amber-400">
      No phone
    </span>
  )
}

export default function OrganizationList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    organizations,
    listTotal,
    listPage,
    listLoading,
    listSummary,
    error,
    clearError,
    fetchOrganizations,
  } = useOrganization()

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const statusFilter = searchParams.get('status') ?? ''
  const missingPhoneFilter = searchParams.get('missingPhone') === '1'
  const missingPhoneCount = Number(listSummary?.missingPhone ?? 0)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQ(searchInput.trim())
      setPage(1)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    fetchOrganizations({
      q: q || undefined,
      status: statusFilter || undefined,
      missingPhone: missingPhoneFilter || undefined,
      page,
      limit: PAGE_SIZE,
    }).catch(() => {})
  }, [fetchOrganizations, q, page, statusFilter, missingPhoneFilter])

  function setMissingPhoneFilter(on: boolean) {
    setPage(1)
    const params = new URLSearchParams(searchParams)
    if (on) params.set('missingPhone', '1')
    else params.delete('missingPhone')
    setSearchParams(params, { replace: true })
  }

  function setStatusFilter(next: string) {
    setPage(1)
    const params = new URLSearchParams(searchParams)
    if (next) params.set('status', next)
    else params.delete('status')
    setSearchParams(params, { replace: true })
  }

  const totalPages = Math.max(1, Math.ceil(listTotal / PAGE_SIZE))
  const showEmpty = !listLoading && organizations.length === 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Organizations"
          description={`View and manage every business using ${APP_NAME}. Open one to see full details and stores.`}
          action={
            <Link to="/organizations/new">
              <Button type="button">Create organization</Button>
            </Link>
          }
        />

        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[12rem] flex-1 max-w-md">
            <Input
              id="org-search"
              type="search"
              label="Search organizations"
              placeholder="Name, email, slug, country, subdomain…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
            {q ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {listLoading ? 'Searching…' : `${listTotal} result${listTotal === 1 ? '' : 's'}`}
              </p>
            ) : null}
          </div>
          <label className="flex min-w-[11rem] flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</span>
            <select
              className="min-h-10 rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {ORG_STATUS_OPTIONS.map((f) => (
                <option key={f.value || 'all'} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {missingPhoneFilter || missingPhoneCount > 0 ? (
          <div
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground"
            role="status"
          >
            <span>
              {missingPhoneFilter
                ? 'Showing only organizations without a contact phone number.'
                : `${missingPhoneCount} organization${missingPhoneCount === 1 ? ' has' : 's have'} no contact phone number yet.`}
            </span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setMissingPhoneFilter(!missingPhoneFilter)}
            >
              {missingPhoneFilter ? 'Show all' : 'Show them'}
            </Button>
          </div>
        ) : null}

        {error ? (
          <div className="mb-4 flex items-center justify-between gap-2 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <span>{error}</span>
            <button type="button" className="cursor-pointer font-medium underline" onClick={clearError}>
              Dismiss
            </button>
          </div>
        ) : null}

        {listLoading && organizations.length === 0 ? (
          <SkeletonTable rows={8} columns={7} />
        ) : showEmpty ? (
          <p className="rounded-2xl border border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
            {missingPhoneFilter && !q && !statusFilter
              ? 'Every organization has a contact phone number.'
              : q || statusFilter || missingPhoneFilter
                ? 'No organizations match your filters.'
                : 'No organizations yet.'}
          </p>
        ) : (
          <>
            <div className="hidden md:block">
              <Table variant="plain">
                <TableHeader>
                  <TableRow>
                    <TableHead>S/N</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead align="right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((o, rowIndex) => (
                    <TableRow key={o.id}>
                      <TableCell className="tabular-nums">
                        {(listPage - 1) * PAGE_SIZE + rowIndex + 1}
                      </TableCell>
                      <TableCell className="font-medium text-foreground">
                        {o.name}
                        {!o.phone?.trim() ? <NoPhoneBadge /> : null}
                      </TableCell>
                      <TableCell>{o.email}</TableCell>
                      <TableCell>{o.country}</TableCell>
                      <TableCell>{getCurrencyLabel(o.currency)}</TableCell>
                      <TableCell>
                        <OrgStatusBadge status={o.status} />
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex justify-end gap-3">
                          <Link
                            to={`/organizations/${o.id}/edit`}
                            className="cursor-pointer font-medium text-foreground transition-colors hover:text-primary"
                          >
                            Edit
                          </Link>
                          <Link
                            to={`/organizations/${o.id}`}
                            className="cursor-pointer font-medium text-primary transition-colors hover:text-primary/80"
                          >
                            View
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="grid gap-4 md:hidden">
              {organizations.map((o, rowIndex) => (
                <div
                  key={o.id}
                  className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-2.5">
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-semibold tabular-nums text-foreground ring-1 ring-border"
                        aria-label={`S/N ${(listPage - 1) * PAGE_SIZE + rowIndex + 1}`}
                      >
                        {(listPage - 1) * PAGE_SIZE + rowIndex + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground">
                          {o.name}
                          {!o.phone?.trim() ? <NoPhoneBadge /> : null}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-muted-foreground">{o.email}</p>
                      </div>
                    </div>
                    <OrgStatusBadge status={o.status} />
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs font-medium text-muted-foreground">Country</dt>
                      <dd className="mt-0.5 text-foreground">{o.country}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-muted-foreground">Currency</dt>
                      <dd className="mt-0.5 text-foreground">{getCurrencyLabel(o.currency)}</dd>
                    </div>
                  </dl>
                  <div className="mt-5 flex gap-2">
                    <Link to={`/organizations/${o.id}/edit`} className="flex-1">
                      <Button type="button" variant="secondary" className="w-full">
                        Edit
                      </Button>
                    </Link>
                    <Link to={`/organizations/${o.id}`} className="flex-1">
                      <Button type="button" className="w-full">
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                <p className="text-sm text-muted-foreground">
                  Page {listPage} of {totalPages} · {listTotal} total
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={listPage <= 1 || listLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={listPage >= totalPages || listLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  )
}
